import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Target,
  Users,
  TrendingUp,
  Shield,
  FolderKanban,
  MessageSquare,
  Code,
  Building,
  ChevronRight,
  Award,
  BookOpen,
  Clock,
  Scale,
  Eye,
  CheckCircle,
  Download,
} from 'lucide-react';
import {
  primeCategories,
  primeLevels,
  primeTargetPolicy,
  ratingLevels,
  scoreToPercent,
  weightedPercent,
  type PrimeCategory,
} from '../data/primeReferentiel';
import SpiderChart from './SpiderChart';
import PrimeRACITable from './PrimeRACITable';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface PrimeReferentielModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const iconMap: Record<string, any> = {
  Target,
  Users,
  TrendingUp,
  Shield,
  FolderKanban,
  MessageSquare,
  Code,
  Building,
};

const levelForScore = (score: number) => {
  if (score >= 4.5) return primeLevels[2];
  if (score >= 3) return primeLevels[1];
  return primeLevels[0];
};

export default function PrimeReferentielModal({ isOpen, onClose }: PrimeReferentielModalProps) {
  const [activeTab, setActiveTab] = useState<'synthese' | 'referentiel' | 'spiders' | 'gouvernance'>('synthese');
  const [selectedCategory, setSelectedCategory] = useState<PrimeCategory | null>(null);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['referentiel_prime_last_ai_analysis'] ?? null) : null;

  const summary = useMemo(() => {
    const skill = Math.round(
      primeCategories.reduce((sum, cat) => {
        const catSkill = cat.subCompetences.reduce((s, sub) => s + sub.skillScore, 0) / cat.subCompetences.length;
        return sum + catSkill;
      }, 0) / primeCategories.length,
    );
    const will = Math.round(
      primeCategories.reduce((sum, cat) => {
        const catWill = cat.subCompetences.reduce((s, sub) => s + sub.willScore, 0) / cat.subCompetences.length;
        return sum + catWill;
      }, 0) / primeCategories.length,
    );
    const combined = Number((primeCategories.reduce((sum, cat) => sum + cat.currentScore, 0) / primeCategories.length).toFixed(2));
    return { skill, will, combined, level: levelForScore(combined) };
  }, []);

  if (!isOpen) return null;

  const exportExcelTemplate = () => {
    const wb = XLSX.utils.book_new();
    const today = new Date().toISOString().split('T')[0];

    const instructions = [
      ['PRIsME — Template Skill / Will Technicien TI / Prime'],
      ['Version', today],
      ['Usage', 'Source de vérité PRIsME pour guider l’évaluation RH. Le gestionnaire complète l’évaluation et la discute avec l’employé en transparence.'],
      ['Échelle', '1=Initial/à risque, 2=Partiel/inconstant, 3=Conforme/autonome, 4=Structuré/proactif, 5=Référence/excellence'],
      ['Règle', 'Chaque score doit être appuyé par une preuve observable ou un exemple concret.'],
      ['Target Skill', primeTargetPolicy.skill],
      ['Target Will', primeTargetPolicy.will],
      ['Lecture', 'Le pourcentage facilite la lecture universelle; l’échelle 1–5 garde la cohérence ITIL.'],
      [],
      ['Niveaux BZ'],
      ['Technicien TI N1', 'Fondations techniques et comportementales en développement'],
      ['Technicien TI N2 / Prime client', 'Autonome, fiable et capable de porter une relation client'],
      ['Technicien TI N3 / Senior Prime', 'Référence technique, relationnelle et comportementale'],
    ];

    const evaluationRows: (string | number)[][] = [[
      'Technicien',
      'Niveau actuel',
      'Pilier',
      'Compétence',
      'Description',
      'Poids Skill',
      'Poids Will',
      'Score Skill ASIS (1-5)',
      'Target Skill réaliste (1-5)',
      'Skill %',
      'Score Will ASIS (1-5)',
      'Target Will réaliste (1-5)',
      'Will %',
      'Score combiné %',
      'Preuves attendues',
      'Preuves observées / commentaires',
      'Plan d’action',
      'Échéance',
    ]];

    const technicien = 'Nom du technicien';
    primeCategories.forEach((category) => {
      category.subCompetences.forEach((sub) => {
        const rowNumber = evaluationRows.length + 1;
        evaluationRows.push([
          technicien,
          '',
          category.name,
          sub.name,
          sub.description,
          category.skillWeight,
          category.willWeight,
          '',
          '',
          { f: `IF(H${rowNumber}=\"\",\"\",H${rowNumber}/5)` } as any,
          '',
          '',
          { f: `IF(K${rowNumber}=\"\",\"\",K${rowNumber}/5)` } as any,
          { f: `IF(OR(H${rowNumber}=\"\",K${rowNumber}=\"\"),\"\",((H${rowNumber}*F${rowNumber})+(K${rowNumber}*G${rowNumber}))/(5*(F${rowNumber}+G${rowNumber})))` } as any,
          sub.evidence.join(' | '),
          '',
          '',
          '',
        ]);
      });
    });

    const scaleRows: (string | number)[][] = [['Pilier', 'Compétence', 'Niveau 1', 'Niveau 2', 'Niveau 3', 'Niveau 4', 'Niveau 5']];
    primeCategories.forEach((category) => {
      category.subCompetences.forEach((sub) => {
        scaleRows.push([category.name, sub.name, sub.scale[1], sub.scale[2], sub.scale[3], sub.scale[4], sub.scale[5]]);
      });
    });

    const weightsRows = [
      ['Pilier', 'Poids Skill', 'Poids Will', 'Logique'],
      ...primeCategories.map((category) => [
        category.name,
        `${category.skillWeight}%`,
        `${category.willWeight}%`,
        category.skillWeight > category.willWeight
          ? 'Pilier davantage technique/méthodologique'
          : category.willWeight > category.skillWeight
            ? 'Pilier davantage posture/constance/fiabilité'
            : 'Équilibre compétence et posture',
      ]),
    ];

    const lastEvaluationRow = evaluationRows.length;
    const summaryRows: (string | number)[][] = [[
      'Technicien',
      'Moyenne Skill %',
      'Moyenne Will %',
      'Moyenne combinée %',
      'Niveau indicatif',
    ], [
      'Nom du technicien',
      { f: `AVERAGE('Évaluation tech'!J2:J${lastEvaluationRow})` } as any,
      { f: `AVERAGE('Évaluation tech'!M2:M${lastEvaluationRow})` } as any,
      { f: `AVERAGE('Évaluation tech'!N2:N${lastEvaluationRow})` } as any,
      { f: 'IF(D2>=0.9,\"Technicien TI N3 / Senior Prime\",IF(D2>=0.6,\"Technicien TI N2 / Prime client\",\"Technicien TI N1\"))' } as any,
    ]];

    const addSheet = (name: string, rows: any[][], widths: number[]) => {
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = widths.map((wch) => ({ wch }));
      XLSX.utils.book_append_sheet(wb, ws, name);
    };

    addSheet('Instructions', instructions, [32, 120]);
    addSheet('Évaluation tech', evaluationRows, [22, 22, 28, 36, 52, 12, 12, 18, 20, 12, 18, 20, 12, 16, 44, 48, 36, 16]);
    addSheet('Résumé', summaryRows, [18, 18, 18, 20, 32]);
    addSheet('Échelle 1-5', scaleRows, [28, 36, 32, 32, 32, 32, 32]);
    addSheet('Pondérations', weightsRows, [30, 14, 14, 52]);

    XLSX.writeFile(wb, `PRIsME_Template_Skill-Will_Technicien_${today}.xlsx`);
  };

  const tabs = [
    { id: 'synthese' as const, label: 'Synthèse RH', icon: Award },
    { id: 'referentiel' as const, label: '8 piliers & compétences', icon: BookOpen },
    { id: 'spiders' as const, label: 'Spiders Skill / Will / Combiné', icon: Target },
    { id: 'gouvernance' as const, label: 'Gouvernance d’évaluation', icon: Scale },
  ];

  return (
    <AnimatePresence>
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 z-40"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 20 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-bz-dark to-bz-blue text-white p-6 rounded-t-xl z-10">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-white/65 font-semibold mb-2">
                    Source de vérité PRIsME — non opérationnelle
                  </p>
                  <h2 className="text-3xl font-bold mb-2">Référentiel Technicien TI / Prime — Standards d’Excellence</h2>
                  <p className="text-white/90 max-w-4xl">
                    Cadre de compétence RH pour Technicien TI N1, N2 et N3. À partir du niveau 2, la ressource devient un
                    <strong> Prime client</strong> dans le vocabulaire interne BZ.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportExcelTemplate}
                    className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/25 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                    title="Exporter le template Excel Skill/Will pour un technicien"
                  >
                    <Download className="w-4 h-4" />
                    Export Excel 1 tech
                  </button>
                  <button onClick={onClose} className="text-white hover:bg-white/10 rounded-full p-2 transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <ScoreCard label="SKILL moyen" score={summary.skill} percent={scoreToPercent(summary.skill)} color="#60A5FA" />
                <ScoreCard label="WILL moyen" score={summary.will} percent={scoreToPercent(summary.will)} color="#C084FC" />
                <ScoreCard label="Score combiné" score={summary.combined} percent={scoreToPercent(summary.combined)} color="#34D399" />
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-sm text-white/70">Niveau indicatif</div>
                  <div className="text-xl font-bold" style={{ color: summary.level.color }}>{summary.level.level}</div>
                  <div className="text-xs text-white/70">{summary.level.description}</div>
                </div>
              </div>

              <div className="mt-6 flex gap-2 overflow-x-auto border-b border-white/20">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-5 py-3 font-semibold transition-all rounded-t-lg whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-white/20 text-white border-b-2 border-white'
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {lastAnalysisDate && (
                <div className="flex items-center gap-2 mt-4 text-xs text-white/80">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                </div>
              )}
            </div>

            {activeTab === 'synthese' && <SyntheseTab />}
            {activeTab === 'referentiel' && <ReferentielTab setSelectedCategory={setSelectedCategory} />}
            {activeTab === 'spiders' && <SpidersTab />}
            {activeTab === 'gouvernance' && <GouvernanceTab />}

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-between items-center border-t">
              <div className="text-sm text-gray-600">
                <strong>Principe:</strong> évaluation neutre, observable, discutée avec l’employé et fondée sur des preuves.
              </div>
              <button onClick={onClose} className="aikup-button">Fermer</button>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {selectedCategory && (
            <CategoryDrawer category={selectedCategory} onClose={() => setSelectedCategory(null)} />
          )}
        </AnimatePresence>
      </>
    </AnimatePresence>
  );
}

function ScoreCard({ label, score, percent, color }: { label: string; score: number; percent: number; color: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
      <div className="text-sm text-white/70">{label}</div>
      <div className="flex items-end gap-2">
        <div className="text-3xl font-bold" style={{ color }}>{score}/5</div>
        <div className="text-lg font-semibold text-white/85">{percent}%</div>
      </div>
      <div className="text-xs text-white/60">Lecture ITIL + % universel</div>
    </div>
  );
}

function SyntheseTab() {
  return (
    <div className="p-6 space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <InfoCard
          title="1. Pourquoi Skill + Will"
          text="Le SKILL dit si la personne est capable. Le WILL dit si elle démontre la constance, la discipline et la posture pour livrer. Un Skill élevé sans Will n’est pas fiable; un Will élevé sans Skill devient un plan de développement."
        />
        <InfoCard
          title="2. Échelle ITIL 1 à 5"
          text="On garde 1 à 5 pour parler le même langage que l’informatique: 1 initial, 3 conforme/autonome, 5 référence. Chaque score est aussi converti en pourcentage pour faciliter la lecture."
        />
        <InfoCard
          title="3. Usage RH transparent"
          text="Le gestionnaire évalue, mais la discussion se fait avec l’employé. Les preuves réduisent l’émotion et rendent l’évaluation défendable, simple et constructive."
        />
      </div>

      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Target className="w-6 h-6 text-amber-600" />
          {primeTargetPolicy.title}
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div className="rounded-xl bg-white border border-amber-200 p-4">
            <div className="font-black text-amber-800 mb-2">Target Skill</div>
            <p>{primeTargetPolicy.skill}</p>
          </div>
          <div className="rounded-xl bg-white border border-amber-200 p-4">
            <div className="font-black text-amber-800 mb-2">Target Will</div>
            <p>{primeTargetPolicy.will}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-gray-700 font-semibold">{primeTargetPolicy.rationale}</p>
        <div className="mt-4 grid md:grid-cols-2 gap-2">
          {primeTargetPolicy.examples.map((example) => (
            <div key={example} className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2 text-xs font-semibold text-gray-700">
              {example}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Scale className="w-6 h-6 text-indigo-600" />
          Pondérations visibles par type de pilier
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <WeightCard title="Technique / performance / stratégie" skill="60%" will="40%" text="Quand la maîtrise technique ou méthodologique est dominante." />
          <WeightCard title="Communication client" skill="40%" will="60%" text="La clarté compte, mais l’intention, l’écoute et la posture font la différence." />
          <WeightCard title="Gouvernance / fiabilité" skill="40%" will="60%" text="Ponctualité, journaux de temps, amélioration continue et rigueur relèvent surtout du Will." />
        </div>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Échelle commune 1–5 → %</h3>
        <div className="grid md:grid-cols-5 gap-3">
          {ratingLevels.map((level) => (
            <div key={level.value} className="rounded-xl border border-gray-200 p-4 bg-gray-50">
              <div className="text-2xl font-black text-bz-blue">{level.value}/5</div>
              <div className="text-lg font-bold text-gray-900">{level.percent}%</div>
              <div className="text-xs text-gray-600 mt-1">{level.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReferentielTab({ setSelectedCategory }: { setSelectedCategory: (category: PrimeCategory) => void }) {
  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {primeCategories.map((category, index) => {
          const Icon = iconMap[category.icon] || Target;
          const combinedPercent = scoreToPercent(category.currentScore);
          return (
            <motion.button
              key={category.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              onClick={() => setSelectedCategory(category)}
              className="bg-white border-2 border-gray-200 rounded-xl p-5 cursor-pointer hover:shadow-lg hover:border-bz-blue transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: `${category.color}20` }}>
                  <Icon className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-gray-900 group-hover:text-bz-blue">{category.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-bz-blue" />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <Metric label="Skill" value={`${category.skillWeight}%`} />
                    <Metric label="Will" value={`${category.willWeight}%`} />
                    <Metric label="Combiné" value={`${combinedPercent}%`} accent />
                  </div>
                  <div className="mt-3 text-xs text-gray-500">
                    {category.subCompetences.length} sous-compétences • seuil attendu {category.minScore}/5 ({scoreToPercent(category.minScore)}%)
                  </div>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function SpidersTab() {
  return (
    <div className="p-6 space-y-6 bg-gray-50">
      <SpiderChart mode="skill" />
      <SpiderChart mode="will" />
      <SpiderChart mode="combined" />
    </div>
  );
}

function GouvernanceTab() {
  return (
    <div className="p-6 space-y-6">
      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Règles d’évaluation neutre et impartiale</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          {[
            'Évaluation faite par le gestionnaire, discutée avec l’employé.',
            'Aucun score sans preuve observable ou exemple concret.',
            'Le score 3/5 représente le niveau conforme/autonome attendu.',
            'Le Will doit être discuté avec transparence: constance, discipline, posture, responsabilisation.',
            'Le score combiné guide la décision, mais le détail Skill/Will explique le plan de développement.',
            'PRIsME documente le standard; l’outil RH opérationnel appliquera ensuite ce cadre.',
          ].map((item) => (
            <div key={item} className="flex items-start gap-2 rounded-lg bg-gray-50 p-3 border border-gray-200">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      <PrimeRACITable />
    </div>
  );
}

function CategoryDrawer({ category, onClose }: { category: PrimeCategory; onClose: () => void }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <motion.div
        initial={{ scale: 0.92, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.92, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[84vh] overflow-y-auto"
      >
        <div className="p-6 text-white rounded-t-xl" style={{ backgroundColor: category.color }}>
          <div className="flex justify-between items-start gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/70 mb-1">Pilier / sous-compétences</p>
              <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
              <p className="text-white/90">{category.description}</p>
              <p className="text-sm text-white/80 mt-2">Pondération: Skill {category.skillWeight}% / Will {category.willWeight}%</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white/10 rounded-full p-2"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {category.subCompetences.map((sub, idx) => {
            const combined = weightedPercent(sub.skillScore, sub.willScore, category.skillWeight, category.willWeight);
            return (
              <motion.div key={sub.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                  <div>
                    <h4 className="font-bold text-gray-900">{sub.name}</h4>
                    <p className="text-sm text-gray-600">{sub.description}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 min-w-[260px] text-xs">
                    <Metric label="Skill" value={`${sub.skillScore}/5`} sub={`${scoreToPercent(sub.skillScore)}%`} />
                    <Metric label="Will" value={`${sub.willScore}/5`} sub={`${scoreToPercent(sub.willScore)}%`} />
                    <Metric label="Final" value={`${combined}%`} accent />
                  </div>
                </div>

                <div className="grid md:grid-cols-5 gap-2 text-xs mb-3">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div key={level} className="bg-white border border-gray-200 rounded-lg p-2">
                      <div className="font-bold text-gray-900">{level}/5</div>
                      <div className="text-gray-600">{sub.scale[level as 1 | 2 | 3 | 4 | 5]}</div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 text-gray-500 font-semibold mr-1"><Eye className="w-3.5 h-3.5" /> Preuves attendues:</span>
                  {sub.evidence.map((evidence) => (
                    <span key={evidence} className="px-2 py-1 rounded-full bg-white border border-gray-200 text-gray-700">{evidence}</span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl p-5">
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
    </div>
  );
}

function WeightCard({ title, skill, will, text }: { title: string; skill: string; will: string; text: string }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-indigo-200">
      <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
      <div className="flex gap-2 mb-2 text-sm">
        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full font-semibold">Skill {skill}</span>
        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full font-semibold">Will {will}</span>
      </div>
      <p className="text-sm text-gray-600">{text}</p>
    </div>
  );
}

function Metric({ label, value, sub, accent = false }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className={`rounded-lg p-2 border ${accent ? 'bg-green-50 border-green-200 text-green-800' : 'bg-white border-gray-200 text-gray-700'}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-black">{value}</div>
      {sub && <div className="text-[10px] opacity-70">{sub}</div>}
    </div>
  );
}
